import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  LinearProgress,
  Fab,
  Zoom,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Stack,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  TextFields as TextIcon,
  ArrowForward as ArrowIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  PhotoLibrary as GalleryIcon,
  Numbers as NumbersIcon,
  Description as DescriptionIcon,
  FileUpload as FileUploadIcon,
  PhotoLibrary,
  GetApp as GetAppIcon,
  Publish as PublishIcon
} from '@mui/icons-material';
import { CoilAlternative, CoilSearchResult } from '../../types/maintenanceBank';
import { CoilAlternativeService } from '../../services/maintenanceBankService';
import { ImageUploadService, ImageAnnotation, AnnotatedImage } from '../../services/imageUploadService';
import ImageAnnotationEditor from '../../components/ImageAnnotationEditor';

interface CoilImage {
  id: string;
  url: string;
  name: string; // اسم الصورة (رقم الكوف)
  number: string; // رقم مقترن بالصورة للبحث
  description?: string;
  annotations: ImageAnnotation[];
  createdAt: Date;
  updatedAt: Date;
  matchType?: string; // نوع التطابق: exact, alternative, similar, etc.
}

const CoilDatasheetPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<CoilImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<CoilImage | null>(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // حالات تحرير الصور
  const [imageAnnotations, setImageAnnotations] = useState<ImageAnnotation[]>([]);
  const [annotatedImages, setAnnotatedImages] = useState<AnnotatedImage[]>(() => {
    // تحميل الصور المحفوظة من localStorage
    const saved = localStorage.getItem('coil_uploaded_images');
    return saved ? JSON.parse(saved) : [];
  });

  // حفظ الصور في localStorage عند التغيير
  useEffect(() => {
    localStorage.setItem('coil_uploaded_images', JSON.stringify(annotatedImages));
  }, [annotatedImages]);

  // وظيفة تصدير البيانات إلى ملف إكسيل
  const exportToExcel = () => {
    try {
      // اختيار مصدر البيانات: نتائج البحث أو الصور المرفوعة محليًا
      const sourceImages: CoilImage[] =
        (searchResults && searchResults.length > 0)
          ? searchResults
          : (annotatedImages || []).map((img, index) => ({
              id: `annotated_${index}_${Date.now()}`,
              url: img.originalUrl,
              name: img.coilNumber || `image_${index + 1}`,
              number: img.coilNumber || '',
              description: img.description || '',
              annotations: img.annotations || [],
              createdAt: img.createdAt || new Date(),
              updatedAt: img.updatedAt || new Date(),
              matchType: 'local'
            }));

      // منع إنشاء ملف فارغ إذا لا توجد بيانات
      if (!sourceImages || sourceImages.length === 0) {
        setError('لا توجد بيانات للتصدير. قم بإظهار النتائج أو إضافة صور.');
        return;
      }

      // تحضير البيانات للتصدير
      const dataToExport = sourceImages.map(image => ({
        'رقم الكوف': image.number,
        'الوصف': image.description || '',
        'رابط الصورة': image.url,
        'اسم الملف': image.name,
        'تاريخ الإنشاء': image.createdAt instanceof Date
          ? image.createdAt.toLocaleString()
          : new Date(image.createdAt).toLocaleString()
      }));

      // إنشاء ورقة عمل وكتاب عمل
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      // تحسين عرض الأعمدة
      (worksheet as any)['!cols'] = [
        { wch: 18 }, // رقم الكوف
        { wch: 40 }, // الوصف
        { wch: 45 }, // رابط الصورة
        { wch: 24 }, // اسم الملف
        { wch: 22 }  // تاريخ الإنشاء
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "بيانات الكوف");

      // حفظ الملف
      XLSX.writeFile(workbook, "coil_datasheet_data.xlsx");
      
      console.log('✅ تم تصدير البيانات بنجاح إلى ملف إكسيل');
      
      // إظهار رسالة نجاح
      setUploadSuccess(true);
    } catch (error) {
      console.error('❌ خطأ في تصدير البيانات:', error);
      setError('فشل في تصدير البيانات: ' + error);
    }
  };


  // وظيفة استيراد البيانات من ملف إكسيل
  const importFromExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // قراءة الورقة الأولى
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // تحويل البيانات إلى مصفوفة من الكائنات
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          console.log('تم استيراد البيانات:', jsonData);
          
          // دالة مساعدة لاستخراج القيم من مفاتيح متعددة (يدعم المسميات بالعربية والإنجليزية)
          const pickValue = (row: any, keys: string[]) => {
            for (const key of keys) {
              if (row[key] !== undefined && String(row[key]).trim() !== '') {
                return String(row[key]).trim();
              }
            }
            return '';
          };

          // معالجة البيانات المستوردة
          const importedImages: AnnotatedImage[] = (jsonData as any[]).map((row: any) => {
            const originalUrl = pickValue(row, [
              'رابط الصورة', 'رابط_الصورة', 'الرابط', 'Link', 'link', 'URL', 'url', 'Image URL', 'imageUrl'
            ]);
            const coilNumber = pickValue(row, [
              'رقم الكوف', 'رقم_الكوف', 'رقم', 'Number', 'number', 'اسم الملف', 'name'
            ]);
            const description = pickValue(row, [
              'الوصف', 'وصف', 'Description', 'description'
            ]);

            return {
              originalUrl,
              annotations: [],
              coilNumber,
              description,
              createdAt: new Date(),
              updatedAt: new Date()
            } as AnnotatedImage;
          })
          // تجاهل الصفوف التي لا تحتوي على رابط صورة
          .filter(img => img.originalUrl && img.originalUrl.length > 0);
          
          // إضافة الصور المستوردة إلى القائمة الحالية
          setAnnotatedImages(prev => [...prev, ...importedImages]);
          
          // تحديث نتائج البحث
          const importedSearchResults: CoilImage[] = importedImages.map((img, index) => ({
            id: `imported_${index}_${Date.now()}`,
            url: img.originalUrl,
            name: img.coilNumber || `image_${index + 1}`,
            number: img.coilNumber || '',
            description: img.description || '',
            annotations: [],
            createdAt: new Date(),
            updatedAt: new Date(),
            matchType: 'exact'
          }));
          
          setSearchResults(prev => [...prev, ...importedSearchResults]);
          
          console.log('✅ تم استيراد البيانات بنجاح');
        } catch (error) {
          console.error('❌ خطأ في معالجة ملف الإكسيل:', error);
        }
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('❌ خطأ في استيراد البيانات:', error);
    }
  };

  // البحث عن الصور المطابقة
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      // عرض جميع الصور المحفوظة إذا لم يتم إدخال شيء
      const allImages: CoilImage[] = annotatedImages.map(image => ({
        id: `uploaded_${image.originalUrl}`,
        url: image.originalUrl,
        name: image.coilNumber,
        number: image.coilNumber, // استخدام رقم الكوف كرقم مقترن
        description: image.description,
        annotations: image.annotations,
        createdAt: image.createdAt,
        updatedAt: image.updatedAt
      }));
      
      setSearchResults(allImages);
      
      if (allImages.length === 0) {
        setError('لا توجد صور محفوظة. ارفع صورة جديدة أولاً.');
      } else {
        setError(null);
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const images: CoilImage[] = [];
      const searchTermLower = searchTerm.trim().toLowerCase();
      
      // البحث في الكوفات أولاً
      try {
        const coilResults = await CoilAlternativeService.searchCoilByNumber(searchTerm.trim());
        
        coilResults.forEach(result => {
          // إضافة صور الكوف
          result.coil.images.forEach((image, index) => {
            images.push({
              id: `${result.coil.id}_image_${index}`,
              url: image.url,
              name: result.coil.originalCoilNumber,
              number: result.coil.originalCoilNumber, // استخدام رقم الكوف الأصلي كرقم مقترن
              description: image.description || `صورة الكوف ${result.coil.originalCoilNumber}`,
              annotations: [],
              createdAt: result.coil.createdAt,
              updatedAt: result.coil.updatedAt
            });
          });
        });
      } catch (coilError) {
        console.log('لا توجد كوفات مطابقة في قاعدة البيانات');
      }

      // إضافة الصور المرفوعة سابقاً (دائماً)
      const uploadedImages = getCoilImages(searchTerm);
      console.log('الصور المرفوعة للكوف', searchTerm, ':', uploadedImages.length);
      
      uploadedImages.forEach(image => {
        images.push({
          id: `uploaded_${image.originalUrl}`,
          url: image.originalUrl,
          name: image.coilNumber,
          number: image.coilNumber, // استخدام رقم الكوف كرقم مقترن
          description: image.description,
          annotations: image.annotations,
          createdAt: image.createdAt,
          updatedAt: image.updatedAt
        });
      });

      // البحث في جميع الصور المرفوعة (حتى لو لم تكن مطابقة تماماً)
      const allUploadedImages = annotatedImages.filter(img => 
        img.coilNumber.toLowerCase().includes(searchTermLower) ||
        img.description?.toLowerCase().includes(searchTermLower)
      );
      
      allUploadedImages.forEach(image => {
        // تجنب التكرار
        const existingImage = images.find(img => img.url === image.originalUrl);
        if (!existingImage) {
          images.push({
            id: `uploaded_${image.originalUrl}`,
            url: image.originalUrl,
            name: image.coilNumber,
            number: image.coilNumber, // استخدام رقم الكوف كرقم مقترن
            description: image.description,
            annotations: image.annotations,
            createdAt: image.createdAt,
            updatedAt: image.updatedAt
          });
        }
      });

      // ترتيب النتائج: الأرقام المطابقة تماماً أولاً، ثم المطابقة الجزئية
      images.sort((a, b) => {
        const aExactMatch = a.number.toLowerCase() === searchTermLower;
        const bExactMatch = b.number.toLowerCase() === searchTermLower;
        
        if (aExactMatch && !bExactMatch) return -1;
        if (!aExactMatch && bExactMatch) return 1;
        
        // ثم ترتيب حسب التاريخ (الأحدث أولاً)
        // التحقق من أن createdAt هو كائن Date قبل استخدام getTime
        const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
        const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
        return bTime - aTime;
      });

      setSearchResults(images);
      
      console.log('إجمالي الصور الموجودة:', images.length);
      
      if (images.length === 0) {
        setError('لم يتم العثور على صور للرقم المدخل. جرب البحث بأجزاء مختلفة من الرقم أو ارفع صورة جديدة.');
      } else {
        setError(null); // مسح أي أخطاء سابقة
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (image: CoilImage) => {
    setSelectedImage(image);
    setImageAnnotations(image.annotations);
    setShowImageEditor(true);
  };

  const handleEditImage = (image: CoilImage) => {
    setSelectedImage(image);
    setImageAnnotations(image.annotations);
    setShowImageEditor(true);
  };

  const handleSaveAnnotations = () => {
    if (selectedImage) {
      // تحديث الصورة المحددة
      const updatedImages = searchResults.map(img =>
        img.id === selectedImage.id
          ? { ...img, annotations: imageAnnotations, updatedAt: new Date() }
          : img
      );
      setSearchResults(updatedImages);

      // تحديث الصور المرفوعة
      const updatedAnnotatedImages = annotatedImages.map(img =>
        img.originalUrl === selectedImage.url
          ? { ...img, annotations: imageAnnotations, updatedAt: new Date() }
          : img
      );
      setAnnotatedImages(updatedAnnotatedImages);

      setShowImageEditor(false);
    }
  };

  // إضافة متغيرات حالة جديدة
  const [uploadImageNumber, setUploadImageNumber] = useState('');
  const [uploadImageDescription, setUploadImageDescription] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // فتح نافذة رفع صورة جديدة
  const openUploadDialog = () => {
    setUploadImageNumber('');
    setUploadImageDescription('');
    setShowUploadDialog(true);
  };

  // إغلاق نافذة رفع صورة جديدة
  const closeUploadDialog = () => {
    setShowUploadDialog(false);
  };

  // اختيار ملف للرفع
  const selectFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // متغير عام لتخزين الملف المختار
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // معالجة اختيار الملف
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    // التحقق من صحة الملف
    const validation = ImageUploadService.validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || 'ملف غير صالح');
      return;
    }

    // حفظ الملف المختار
    setSelectedFile(file);
    
    // فتح نافذة الرفع لإدخال الرقم والوصف
    openUploadDialog();
  };

  // وظيفة رفع الصورة مع الرقم المقترن
  const uploadImageWithNumber = async () => {
    // التحقق من وجود ملف وإدخال رقم
    if (!selectedFile) {
      setError('يرجى اختيار صورة أولاً');
      return;
    }

    if (!uploadImageNumber.trim()) {
      setError('يجب إدخال رقم مقترن بالصورة');
      return;
    }
    
    console.log('بدء رفع الصورة مع الرقم المقترن:', uploadImageNumber);

    try {
      setUploadingImage(true);
      setUploadProgress(0);
      setError(null);

      console.log('📤 بدء رفع صورة الكوف:', {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type,
        imageNumber: uploadImageNumber
      });

      // محاكاة تقدم الرفع
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // الرفع عبر الخدمة الموحدة وفق إعدادات صفحة الإعدادات المتقدمة
      const result = await ImageUploadService.uploadForMaintenanceBank(selectedFile);
      console.log('✅ نجح الرفع عبر الخدمة الموحدة:', result);

      clearInterval(progressInterval);
      setUploadProgress(100);

      const description = uploadImageDescription.trim() || `صورة رقم ${uploadImageNumber}`;

      // إضافة الصورة للكوف
      const newAnnotatedImage: AnnotatedImage = {
        originalUrl: result.url,
        annotations: [],
        coilNumber: uploadImageNumber.trim(),
        description: description,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setAnnotatedImages(prev => [...prev, newAnnotatedImage]);


      // إضافة الصورة للنتائج
      const newImage: CoilImage = {
        id: `uploaded_${result.url}`,
        url: result.url,
        name: uploadImageNumber,
        number: uploadImageNumber,
        description: description,
        annotations: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        matchType: 'exact' // إضافة نوع التطابق للصورة الجديدة
      };

      setSearchResults(prev => [...prev, newImage]);

      console.log('✅ تم رفع الصورة بنجاح:', {
        url: result.url,
        imageNumber: uploadImageNumber,
        totalImages: searchResults.length + 1
      });

      // إغلاق نافذة الرفع وإظهار رسالة نجاح
      setShowUploadDialog(false);
      setUploadSuccess(true);
      
      // تحديث البحث تلقائياً بالرقم المقترن بالصورة
        if (uploadImageNumber.trim()) {
          setSearchTerm(uploadImageNumber.trim());
          handleSearch();
        }

      // إعادة تعيين حقول الإدخال
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setUploadImageNumber('');
      setUploadImageDescription('');

    } catch (error: any) {
      console.error('❌ خطأ في رفع الصورة:', error);
      
      let errorMessage = 'فشل في رفع الصورة';
      
      if (error.message.includes('API key')) {
        errorMessage = 'مفتاح API غير صحيح أو مفقود. يرجى التحقق من إعدادات رفع الصور.';
      } else if (error.message.includes('network')) {
        errorMessage = 'مشكلة في الاتصال بالإنترنت. يرجى المحاولة مرة أخرى.';
      } else if (error.message.includes('size')) {
        errorMessage = 'حجم الصورة كبير جداً. الحد الأقصى 5 ميجابايت.';
      } else if (error.message.includes('type')) {
        errorMessage = 'نوع الملف غير مدعوم. الأنواع المدعومة: JPEG, PNG, GIF, WebP.';
      } else {
        errorMessage = `خطأ في رفع الصورة: ${error.message}`;
      }
      
      // محاولة حفظ الصورة محليًا كحل بديل
      try {
        if (selectedFile) {
          const dataUrl: string = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(selectedFile);
          });

          const description = uploadImageDescription.trim() || `صورة رقم ${uploadImageNumber}`;

          const newAnnotatedImage: AnnotatedImage = {
            originalUrl: dataUrl,
            annotations: [],
            coilNumber: uploadImageNumber.trim(),
            description: description,
            createdAt: new Date(),
            updatedAt: new Date()
          };

          setAnnotatedImages(prev => [...prev, newAnnotatedImage]);

          const newImage: CoilImage = {
            id: `local_${Date.now()}`,
            url: dataUrl,
            name: uploadImageNumber,
            number: uploadImageNumber,
            description: description,
            annotations: [],
            createdAt: new Date(),
            updatedAt: new Date(),
            matchType: 'exact'
          };

          setSearchResults(prev => [...prev, newImage]);

          // إغلاق النافذة لأن الصورة تم حفظها محليًا
          setShowUploadDialog(false);
          setUploadSuccess(true);
          setError(`${errorMessage} — تم حفظ الصورة محليًا مؤقتًا. قم بإعداد الخدمة الموحدة عبر صفحة الإعدادات المتقدمة: الإدارة > الإعدادات المتقدمة.`);

          // إعادة تعيين الإدخالات
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          setUploadImageNumber('');
          setUploadImageDescription('');
        } else {
          setError(errorMessage);
        }
      } catch (fallbackError: any) {
        console.error('❌ فشل الحفظ المحلي للصورة:', fallbackError);
        setError(errorMessage);
      }
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
    }
  };

  const getCoilImages = (coilNumber: string) => {
    // البحث عن تطابق تام أو جزئي
    return annotatedImages.filter(img => 
      img.coilNumber.toLowerCase() === coilNumber.toLowerCase() ||
      img.coilNumber.toLowerCase().includes(coilNumber.toLowerCase()) ||
      coilNumber.toLowerCase().includes(img.coilNumber.toLowerCase())
    );
  };

  // حالة رسائل التأكيد والتراجع للحذف
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [lastDeletedImage, setLastDeletedImage] = useState<CoilImage | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<CoilImage | null>(null);

  // حذف صورة فردياً (محلياً) مع دعم التراجع
  const handleDeleteImage = (image: CoilImage) => {
    const isLocalImage =
      image.id.startsWith('local_') ||
      image.id.startsWith('uploaded_') ||
      (image.matchType === 'local') ||
      (image.url?.startsWith('data:'));

    if (!isLocalImage) {
      setError('لا يمكن حذف صور قاعدة البيانات من هنا. يمكن إدارة صور الكوف من صفحة البدائل.');
      return;
    }

    // حفظ آخر صورة محذوفة لاسترجاعها عند التراجع
    setLastDeletedImage(image);

    // إغلاق محرر الصورة إن كانت الصورة المحددة هي نفسها
    if (selectedImage && (selectedImage.id === image.id || selectedImage.url === image.url)) {
      setShowImageEditor(false);
      setSelectedImage(null);
    }

    // إزالة من الصور المرفوعة محلياً وتحديث التخزين المحلي
    setAnnotatedImages(prev => {
      const updated = prev.filter(img => img.originalUrl !== image.url);
      localStorage.setItem('coil_uploaded_images', JSON.stringify(updated));
      return updated;
    });

    // إزالة من نتائج البحث
    setSearchResults(prev => prev.filter(img => img.id !== image.id && img.url !== image.url));

    // إظهار رسالة نجاح مع خيار التراجع
    setSnackbarMessage('تم حذف الصورة');
    setSnackbarOpen(true);
  };

  const handleUndoDelete = () => {
    if (!lastDeletedImage) return;

    // استرجاع الصورة إلى النتائج
    setSearchResults(prev => {
      const exists = prev.some(img => img.id === lastDeletedImage!.id || img.url === lastDeletedImage!.url);
      return exists ? prev : [lastDeletedImage!, ...prev];
    });

    // إذا كانت الصورة محلية، استرجعها أيضاً إلى الصور المرفوعة
    setAnnotatedImages(prev => {
      const exists = prev.some(img => img.originalUrl === lastDeletedImage!.url);
      const restored = exists ? prev : [{
        originalUrl: lastDeletedImage!.url,
        coilNumber: lastDeletedImage!.number,
        description: lastDeletedImage!.description || '',
        annotations: lastDeletedImage!.annotations,
        createdAt: lastDeletedImage!.createdAt,
        updatedAt: lastDeletedImage!.updatedAt
      }, ...prev];
      localStorage.setItem('coil_uploaded_images', JSON.stringify(restored));
      return restored;
    });

    setSnackbarMessage('تم استرجاع الصورة');
    setSnackbarOpen(true);
    setLastDeletedImage(null);
  };

  const getMatchTypeColor = (matchType: string) => {
    switch (matchType) {
      case 'exact':
        return 'success';
      case 'alternative':
        return 'primary';
      case 'similar':
        return 'warning';
      case 'image_name':
        return 'info';
      case 'partial_original':
        return 'secondary';
      case 'partial_alternative':
        return 'warning';
      case 'description':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      {/* شريط البحث */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <GalleryIcon fontSize="large" /> داتا شيت الكوفات
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          ابحث عن صور الكوفات والمكونات باستخدام الأرقام المقترنة بها، وأضف صور جديدة مع أرقام للبحث
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Chip 
            icon={<PhotoLibrary />}
            label={`${annotatedImages.length} صورة محفوظة`}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 1 }}
          />
          <Chip 
            icon={<SearchIcon />}
            label={`${searchResults.length} نتيجة بحث`}
            color="secondary"
            variant="outlined"
            sx={{ borderRadius: 1 }}
          />
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="رقم الكوف أو المكون"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="أدخل الرقم المقترن بالصورة للبحث"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <NumbersIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Tooltip title="ابحث باستخدام الرقم المقترن بالصورة" arrow>
              <span>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  fullWidth
                  sx={{ py: 1.5, borderRadius: 1.5, height: 56 }}
                >
                  بحث
                </Button>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={2}>
            <Tooltip title="أضف صورة مع رقم لسهولة البحث" arrow>
              <span>
                <Button
                  variant="outlined"
                  onClick={openUploadDialog}
                  startIcon={uploadingImage ? <CircularProgress size={20} /> : <FileUploadIcon />}
                  disabled={loading || uploadingImage}
                  fullWidth
                  color="success"
                  sx={{ py: 1.5, borderRadius: 1.5, height: 56 }}
                >
                  إضافة داتا شيت كوف
                </Button>
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={12} md={2}>
            <Tooltip title="اعرض كل الصور المحفوظة" arrow>
              <span>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<VisibilityIcon />}
                  onClick={() => {
                    setSearchTerm('');
                    handleSearch();
                  }}
                  fullWidth
                  sx={{ height: 56 }}
                >
                  عرض كل الصور
                </Button>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={2}>
            <Tooltip title="صدّر النتائج أو الصور المحفوظة إلى ملف إكسيل" arrow>
              <span>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<GetAppIcon />}
                  onClick={exportToExcel}
                  fullWidth
                  sx={{ height: 56 }}
                >
                  تصدير إلى إكسيل
                </Button>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={2}>
            <Tooltip title="استورد بيانات الصور من ملف إكسيل" arrow>
              <span>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<PublishIcon />}
                  component="label"
                  fullWidth
                  sx={{ height: 56 }}
                >
                  استيراد من إكسيل
                  <input
                    type="file"
                    hidden
                    accept=".xlsx, .xls"
                    onChange={importFromExcel}
                  />
                </Button>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={2}>
            <Tooltip title="امسح الصور المحفوظة محليًا" arrow>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    if (window.confirm('هل تريد مسح جميع الصور المحفوظة؟')) {
                      setAnnotatedImages([]);
                      setSearchResults([]);
                      localStorage.removeItem('coil_uploaded_images');
                    }
                  }}
                  fullWidth
                  sx={{ height: 56 }}
                >
                  مسح الصور
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>

        {/* شريط تقدم الرفع */}
        {uploadingImage && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 4 }} />
            <Typography variant="caption" align="center" display="block" sx={{ mt: 1 }}>
              جاري رفع الصورة... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </Paper>

      {/* رسائل الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* نتائج البحث - عرض الصور */}
      {searchResults.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            الصور المطابقة ({searchResults.length})
          </Typography>
          
          <ImageList cols={3} rowHeight={200} gap={16}>
            {searchResults.map((image) => (
              <ImageListItem key={image.id} sx={{ 
                border: '2px solid transparent',
                borderRadius: 2,
                overflow: 'hidden',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'scale(1.02)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}>
                <img
                  src={image.url}
                  alt={image.description}
                  loading="lazy"
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleViewDetails(image)}
                />
                <ImageListItemBar
                  title={image.name}
                  subtitle={image.description}
                  actionIcon={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="تحرير الصورة">
                        <IconButton
                          sx={{ color: 'white' }}
                          onClick={() => handleEditImage(image)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف الصورة">
                        <IconButton
                          sx={{ color: 'white' }}
                          onClick={() => { setImageToDelete(image); setConfirmDeleteOpen(true); }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Chip
                        label={`${image.annotations.length} تعليق`}
                        size="small"
                        color={image.annotations.length > 0 ? "primary" : "default"}
                        sx={{ mr: 1 }}
                      />
                    </Box>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        </Paper>
      )}

      {/* رسالة عدم وجود نتائج */}
      {!loading && searchTerm && searchResults.length === 0 && !error && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            لم يتم العثور على صور للكوف "{searchTerm}"
          </Typography>
          <Typography variant="body2" color="text.secondary">
            جرب البحث بأجزاء مختلفة من الرقم أو ارفع صورة جديدة
          </Typography>
        </Paper>
      )}

      {/* محرر الصور */}
      <Dialog
        open={showImageEditor}
        onClose={() => setShowImageEditor(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          تحرير صورة الكوف: {selectedImage?.name}
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <ImageAnnotationEditor
              imageUrl={selectedImage.url}
              annotations={imageAnnotations}
              onAnnotationsChange={setImageAnnotations}
              height={500}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowImageEditor(false)}>
            إلغاء
          </Button>
          <Button 
            onClick={handleSaveAnnotations}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            حفظ التحريرات
          </Button>
        </DialogActions>
      </Dialog>

      {/* نافذة إضافة داتا شيت كوف */}
      <Dialog
        open={showUploadDialog}
        onClose={closeUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          إضافة داتا شيت كوف
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required error={!uploadImageNumber.trim() && error?.includes('رقم')}>
              <InputLabel htmlFor="upload-number">رقم الكوف أو المكون</InputLabel>
              <OutlinedInput
                id="upload-number"
                value={uploadImageNumber}
                onChange={(e) => setUploadImageNumber(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <NumbersIcon />
                  </InputAdornment>
                }
                label="رقم الكوف أو المكون"
              />
              {!uploadImageNumber.trim() && error?.includes('رقم') && (
                <FormHelperText error>يجب إدخال رقم مقترن بالصورة</FormHelperText>
              )}
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel htmlFor="upload-description">وصف الصورة (اختياري)</InputLabel>
              <OutlinedInput
                id="upload-description"
                value={uploadImageDescription}
                onChange={(e) => setUploadImageDescription(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <DescriptionIcon />
                  </InputAdornment>
                }
                label="وصف الصورة (اختياري)"
              />
            </FormControl>
            
            <Button
              variant="outlined"
              component="label"
              startIcon={<FileUploadIcon />}
              fullWidth
              sx={{ mt: 1, height: 56 }}
            >
              اختر صورة
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
                ref={fileInputRef}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadDialog} color="inherit">
            إلغاء
          </Button>
          <Button 
            id="upload-image-button"
            variant="contained"
            color="primary"
            disabled={uploadingImage || !uploadImageNumber.trim() || !selectedFile}
            onClick={uploadImageWithNumber}
            startIcon={uploadingImage ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            رفع الصورة
          </Button>
        </DialogActions>
      </Dialog>

      {/* مربع تأكيد الحذف */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>هل تريد حذف هذه الصورة؟</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>لا</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (imageToDelete) handleDeleteImage(imageToDelete);
              setConfirmDeleteOpen(false);
              setImageToDelete(null);
            }}
          >
            نعم
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar للتأكيد والتراجع */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        action={
          <Button color="secondary" size="small" onClick={handleUndoDelete}>
            تراجع
          </Button>
        }
      />

    </Box>
  );
};

export default CoilDatasheetPage;